import { defineStore } from 'pinia'
import {
  AddLeadToCompanyCommandDto,
  GetCompanyFromAdminEmployerDto,
  GetCompanyFromAdminResult,
  ListCompaniesFromAdminCompanyDto,
} from '@swipe4work/models-orval'
import {
  getAdminCompany,
  addLeadToCompany as addLeadToCompanyApi,
  deleteCompany as deleteCompanyApi,
  markAsFollowedUp as markAsFollowedUpApi,
  setManagedCompany,
} from '@swipe4work/api-orval'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { useConfirm } from 'primevue/useconfirm'

// create separate type for company result, so if API names change, we only need to change it in one place
export type ViewCompanyType = GetCompanyFromAdminResult
export type ListCompanyType = ListCompaniesFromAdminCompanyDto
export type CompanyEmployeeType = GetCompanyFromAdminEmployerDto

export const useCompanyStore = defineStore('company', {
  state: () => ({
    portalUrl: import.meta.env.VITE_PORTAL_FRONTEND_URL ?? 'https://portal.test.swipe4work.com',
  }),
  actions: {
    async deleteCompany(id: number, confirm: ReturnType<typeof useConfirm>, redirectBack: boolean = false): Promise<void> {
      confirm.require({
        message: 'Weet je zeker dat je dit bedrijf wilt verwijderen?',
        header: 'Weet je het zeker?',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
          label: 'Verwijderen',
          severity: 'danger',
          outlined: true,
        },
        acceptProps: {
          label: 'Terug',
          severity: 'secondary',
        },
        reject: async () => {
          const [ err ] = await safeResolve(deleteCompanyApi({ companyId: id }))
          if (!err) {
            this.toast.success('Bedrijf verwijderd', 'Het bedrijf is succesvol verwijderd')
            if (redirectBack) {
              this.router.back()
            }
          }
        },
        accept: () => {
        },
      })
    },
    async getCompany(id: number): Promise<ViewCompanyType | undefined> {
      const [ err, res ] = await safeResolve(getAdminCompany(id))

      if (err) return undefined

      return res
    },
    async addLeadToCompany(req: AddLeadToCompanyCommandDto) {
      const [ err ] = await safeResolve(addLeadToCompanyApi(req))

      return !err
    },
    async markFollowUp(company: {
      id: number;
      statistics: { accountManagerFollowedUpAt?: Date | string | null } 
    }): Promise<void> {
      const [ err ] = await safeResolve(markAsFollowedUpApi(company.id))
      if (!err) {
        this.toast.success('Bedrijf opgevolgd', 'Het bedrijf is succesvol opgevolgd')
        company.statistics.accountManagerFollowedUpAt = new Date()
      }
    },
    async forwardToPortal(companyId: number, path: string = '', params: Record<string, string | null | undefined> = {}): Promise<void> {
      /*
       * Open the tab synchronously, still within the click's user-gesture call stack. Opening it
       * after the awaits below would get silently blocked by most popup blockers.
       */
      const tab = window.open('', '_blank')

      await setManagedCompany({ id: companyId })

      const portalUrl = this.portalUrl

      /*
       * Pass only the username as a login hint, not the id token: the portal never trusts this
       * value as a credential, it only uses it to skip the account picker on its own MSAL
       * redirect. Forwarding the actual id token would needlessly put an identity token in a URL
       * (browser history, server logs, error-tracker breadcrumbs).
       */
      const loginHint = useAuthStore().user?.username
      if (!loginHint) {
        tab?.close()

        this.toast.add({
          severity: 'error',
          summary: 'Whoops! Er ging iets mis',
          detail: 'We hebben je niet kunnen inloggen in het portaal. Probeer de pagina opnieuw te laden en probeer het opnieuw.',
          life: 10000,
        })

        return
      }

      // removes leading and trailing slashes
      path = path.replace(/^\/|\/$/g, '')

      // Create the base URL
      const url = new URL(`${portalUrl}/${path}`)

      url.searchParams.set('auth-provider', 'Msal')
      url.searchParams.set('redirect-token', loginHint)

      // Append query parameters
      Object.entries(params).forEach(([ key, value ]) => {
        if (value != null) { // Checks for null or undefined
          url.searchParams.set(key, value)
        }
      })

      if (tab) {
        tab.location.href = url.toString()
      } else {
        window.open(url, '_blank')
      }
    },
  },
})
